import { Id } from "@/fx/Id";
import type { CommandExecutionContext } from '@/be/context';

export const COMMAND_EXECUTED_IMPLICITLY = undefined;

type Context = { data: {} };

// #region Command Descriptor

export type CommandDescriptor<TCommand, TRawCommand, TResult> = {
  commandName:      string,
  commandToRaw:     (command: TCommand                    ) => TRawCommand,
  commandFromRaw:   (raw:     TRawCommand                 ) => TCommand,
  contextProvider?: (result: bigint                       ) => Promise<Context>,
}
export function commandDescriptor<TCommand, TRawCommand, TResult>({ commandName, commandToRaw, commandFromRaw, contextProvider }: {
  commandName:      string,
  commandToRaw:     (command: TCommand                    ) => TRawCommand
  commandFromRaw:   (raw:     TRawCommand                 ) => TCommand,
  contextProvider?: (result: bigint                       ) => Promise<Context>,
}): CommandDescriptor<TCommand, TRawCommand, TResult> {
  return { commandName, commandToRaw, commandFromRaw, contextProvider };
}
export function createCommandDescriptor<TCommand, TRawCommand>({ commandName, commandToRaw, commandFromRaw, contextProvider }: {
  commandName:      string,
  commandToRaw:     (command: TCommand                    ) => TRawCommand
  commandFromRaw:   (raw: TRawCommand                     ) => TCommand
  contextProvider?: (result: bigint                       ) => Promise<Context>,
}): CommandDescriptor<TCommand, TRawCommand, bigint> {
  return  commandDescriptor<TCommand, TRawCommand, bigint>({ commandName, commandToRaw, commandFromRaw, contextProvider });
}
export function updateCommandDescriptor<TCommand, TRawCommand>({ commandName, commandToRaw, commandFromRaw, contextProvider }: {
  commandName:      string,
  commandToRaw:     (command: TCommand                    ) => TRawCommand
  commandFromRaw:   (raw: TRawCommand                     ) => TCommand
  contextProvider?: (result: bigint                       ) => Promise<Context>,
}): CommandDescriptor<TCommand, TRawCommand, void> {
  return  commandDescriptor<TCommand, TRawCommand, void>({ commandName, commandToRaw, commandFromRaw, contextProvider });
}

// #endregion

type ContextBase = {};

export type TEMP_ContextBase = ContextBase;

export type Contextable<TCommand> = TCommand & { commandExecutionContext?: CommandExecutionContext };

export type ContextWithResourceId<TResourceId extends Id> = { claimId: TResourceId };

// #region Command Provider

export type CommandProvider<TCommand, TRawCommand, TResult, TContext extends ContextBase> = {
  descriptor: CommandDescriptor<TCommand, TRawCommand, TResult>,
  command:    (context: TContext) => TCommand,
}

export type CreateCommandProvider                         = CommandProvider<any, any, bigint, ContextBase       >;
export type UpdateCommandProvider<TResourceId extends Id> = CommandProvider<any, any, void,   ContextWithResourceId<TResourceId>>;

export const commandProvider = <TCommand, TRawCommand, TResult>(
  descriptor: CommandDescriptor<TCommand, TRawCommand, TResult>,
) => <TContext extends ContextBase>({ command }: {
  command: (context: TContext) => TCommand
}): CommandProvider<TCommand, TRawCommand, TResult, TContext> => {
  return { descriptor, command };
};

export function updateCommandProvider<TCommand, TRawCommand>({ commandName, commandToRaw, commandFromRaw, command }: {
  commandName:      string,
  commandToRaw:     (command: TCommand) => TRawCommand
  commandFromRaw:   (raw: TRawCommand) => TCommand
  command:          () => TCommand
}): CommandProvider<TCommand, TRawCommand, any, any> {
  return commandProvider(updateCommandDescriptor<TCommand, TRawCommand>({ commandName, commandToRaw, commandFromRaw }))({ command });
}

// #endregion

// #region Command Test Descriptor

export type TestResourceSpec<TResourceId extends Id> = {}

export type TestDefaults<TCommand, TContext extends TEMP_ContextBase> = {
  contextProvider?: (result: bigint                   ) => Promise<TContext>,
  validator?:       (command: TCommand, ctx: TContext ) => boolean,
  cleaner?:         (result: bigint                   ) => Promise<void>,
};

export type CreateCommandTestDescriptor<TCommand, TRawCommand, TContext extends TEMP_ContextBase> = {
  __type:                   'CreateCommandTestDescriptor',
  commandUnderTestProvider: Contextable<CommandProvider<TCommand, TRawCommand, bigint, {}>>,
  contextProvider?:         (result: bigint                   ) => Promise<TContext>,
  validator?:               (command: TCommand, ctx: TContext ) => boolean,
  cleaner?:                 (result: bigint                   ) => Promise<void>,
};

export type UpdateCommandTestDescriptor<TCommand, TRawCommand, TContext extends TEMP_ContextBase, TResourceId extends Id> = {
  __type:                     'UpdateCommandTestDescriptor',
  testResourceSpec?:          TestResourceSpec<TResourceId>,
  createCommandProvider?:     Contextable<CommandProvider<any, any, bigint, {}>>,
  prepCommandProviders:       UpdateCommandProvider<TResourceId>[],
  commandUnderTestProvider?:  CommandProvider<TCommand, TRawCommand, void, TContext>,
  commandExecutionContext?:   CommandExecutionContext,
  commandForDisplayProvider?: (ctx: TContext                   ) => TCommand,
  contextProvider?:           (result: bigint                  ) => Promise<TContext>,
  validator?:                 (command: TCommand, ctx: TContext) => boolean,
  cleaner?:                   (result: bigint                  ) => Promise<void>,
};

export type CommandTestDescriptor<TCommand, TRawCommand, TResourceId extends Id> = CreateCommandTestDescriptor<TCommand, TRawCommand, any> | UpdateCommandTestDescriptor<TCommand, TRawCommand, any, TResourceId>;

export function isCreateCommandTestDescriptor<TCommand, TRawCommand, TResourceId extends Id>(descriptor: CommandTestDescriptor<TCommand, TRawCommand, TResourceId>): descriptor is CreateCommandTestDescriptor<TCommand, TRawCommand, any> {
  return descriptor.__type === 'CreateCommandTestDescriptor';
}
export function isUpdateCommandTestDescriptor<TCommand, TRawCommand, TContext extends TEMP_ContextBase, TResourceId extends Id>(descriptor: CommandTestDescriptor<TCommand, TRawCommand, TResourceId>): descriptor is UpdateCommandTestDescriptor<TCommand, TRawCommand, TContext, TResourceId> {
  return descriptor.__type === 'UpdateCommandTestDescriptor';
}

export function createCommandTestDescriptor<TCommand, TRawCommand, TContext extends TEMP_ContextBase>({ commandUnderTestProvider, contextProvider, validator, cleaner }: {
  commandUnderTestProvider: Contextable<CommandProvider<TCommand, TRawCommand, bigint, {}>>,
  contextProvider?:         (result: bigint                   ) => Promise<TContext>,
  validator?:               (command: TCommand, ctx: TContext ) => boolean,
  cleaner?:                 (result: bigint                   ) => Promise<void>,
}): CreateCommandTestDescriptor<TCommand, TRawCommand, TContext> {
  return { __type: 'CreateCommandTestDescriptor', commandUnderTestProvider, contextProvider, validator, cleaner };
}

export function updateCommandTestDescriptor<TCommand, TRawCommand, TContext extends TEMP_ContextBase, TResourceId extends Id>({ testResourceSpec, createCommandProvider, prepCommandProviders = [], commandUnderTestProvider, commandExecutionContext, commandForDisplayProvider, contextProvider, validator, cleaner }: {
  testResourceSpec?:          TestResourceSpec<TResourceId>,
  createCommandProvider?:     Contextable<CommandProvider<any, any, bigint, {}>>,
  prepCommandProviders?:      UpdateCommandProvider<TResourceId>[],
  commandUnderTestProvider?:  Contextable<CommandProvider<TCommand, TRawCommand, void, TContext>>,
  commandExecutionContext?:   CommandExecutionContext,
  commandForDisplayProvider?: (ctx: TContext                    ) => TCommand,
  contextProvider?:           (result: bigint                   ) => Promise<TContext>,
  validator?:                 (command: TCommand, ctx: TContext ) => boolean,
  cleaner?:                   (result: bigint                   ) => Promise<void>,
}): UpdateCommandTestDescriptor<TCommand, TRawCommand, TContext, TResourceId> {
  return { __type: 'UpdateCommandTestDescriptor', testResourceSpec, createCommandProvider, prepCommandProviders, commandUnderTestProvider, commandExecutionContext, commandForDisplayProvider, contextProvider, validator, cleaner };
}

// #endregion
